import type { Business } from '@/features/clubs/types/types';
import { getBestImageUrl, getBestVideoUrl } from '@/lib/utils';
import { useState, useEffect } from 'react';
import GoogleMaps from './GoogleMaps';

interface ClubCardProps {
  club: Business;
  onClick?: () => void;
}

export default function ClubCard({ club, onClick }: ClubCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [shouldPlayVideo, setShouldPlayVideo] = useState(false);
  const featuredImage = getBestImageUrl(club.card_image_id, club.media, '/placeholder-club.jpg');
  const videoUrl = getBestVideoUrl(club.card_video_id);

  useEffect(() => {
    if (isHovered && videoUrl) {
      const timer = setTimeout(() => {
        setShouldPlayVideo(true);
      }, 300); // 300ms delay
      return () => clearTimeout(timer);
    } else {
      setShouldPlayVideo(false);
    }
  }, [isHovered, videoUrl]);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <div 
      className="bg-gray-700 rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-300"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-48 overflow-hidden">
        {shouldPlayVideo && videoUrl ? (
          <video
            src={videoUrl}
            autoPlay
            muted
            loop
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to image if video fails to load
              const target = e.target as HTMLVideoElement;
              target.style.display = 'none';
              const img = target.nextElementSibling as HTMLImageElement;
              if (img) img.style.display = 'block';
            }}
          />
        ) : null}
        <img
          src={featuredImage}
          alt={club.name}
          className={`w-full h-full object-cover ${shouldPlayVideo && videoUrl ? 'hidden' : 'block'}`}
          onError={(e) => {
            // Fallback to placeholder if image fails to load
            const target = e.target as HTMLImageElement;
            target.src = '/placeholder-club.jpg';
          }}
        />
        {club.published && (
          <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
            Open
          </div>
        )}
      </div>
      
      <div className="p-4">
        <h3 className="text-lg font-semibold text-white mb-2">{club.name}</h3>
        <p className="text-white text-sm mb-3 line-clamp-2">
          {club.description}
        </p>
        
        <div className="space-y-3">
          <div className="flex items-center text-sm text-white">
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
            </svg>
            <span>{club.location.city}, {club.location.country}</span>
          </div>
          
          <GoogleMaps 
            location={club.location} 
            className="h-32 w-full"
          />
        </div>
      </div>
    </div>
  );
}
